import { Workbox } from "workbox-window";
import { Title } from "@solidjs/meta";
import { Router, Route, Navigate, createAsync } from "@solidjs/router";
import { createEffect, createSignal, lazy, untrack } from "solid-js";
import { createI18n, I18nProvider } from "./i18n";
import type { SceneInfo } from "./TenantTypes";
import French from "../locales/fr.json";
import { createStore, reconcile } from "solid-js/store";
import { useSession, useActiveMemberRole } from "../lib/auth-client";
import AppNeedsUpdate, { appNeedsUpdateDismiss, appNeedsUpdateOk, setAppNeedsUpdate } from "./AppNeedsUpdate";
import { appName, pseudo, setPseudoWithoutDollar, setSession, setUserIsAdmin } from "./signals";
import TenantLayout from "./pages/TenantLayout";
import { preloadSceneElements, preloadServerConfig, preloadTenantConfig } from "./Scene.data";

const Home = lazy(() => import("./Home"));
const ARPage = lazy(() => import("./ARPage"));
const MVPage = lazy(() => import("./MVPage"));
const ARBanner = lazy(() => import("./ARBanner"));
const Room = lazy(() => import("./Room"));
const TenantHome = lazy(() => import("./pages/TenantHome"));
const SimpleEditor = lazy(() => import("./pages/SimpleEditor"));
const Login = lazy(() => import("./pages/Login"));
const Profile = lazy(() => import("./pages/Profile"));
const SavedRooms = lazy(() => import("./pages/SavedRooms"));
const ErrorPage = lazy(() => import("./pages/Error"));
const MagicThanks = lazy(() => import("./pages/MagicThanks"));
const AdminOrganizations = lazy(() => import("./pages/AdminOrganizations"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminStorage = lazy(() => import("./pages/AdminStorage"));
const AdminLibraries = lazy(() => import("./pages/AdminLibraries"));
const LibraryEditor = lazy(() => import("./pages/LibraryEditor"));
const OrganizationSettings = lazy(() => import("./pages/OrganizationSettings"));
const Storage = lazy(() => import("./pages/Storage"));
const Libraries = lazy(() => import("./pages/Libraries"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const Scenarios = lazy(() => import("./pages/Scenarios"));
const TrainingPlans = lazy(() => import("./pages/TrainingPlans"));
const PlanView = lazy(() => import("./pages/PlanView"));

const languageLabels = new Map([
  ["en", "English"],
  ["fr", "Français"],
  ["ko", "한국어"],
]);
const supportedLanguages = ["en", "fr"];

let lang = "en";
let savedLang = localStorage.getItem("lang");
if (savedLang !== null && supportedLanguages.indexOf(savedLang) === -1) {
  savedLang = null;
}
if (savedLang === null) {
  const locale = navigator.language;
  if (locale.startsWith("fr")) {
    lang = "fr";
  }
} else {
  lang = savedLang;
}

const [language, setLanguage] = createSignal(lang);
export { language, setLanguage, supportedLanguages, languageLabels };

export const i18nCatalog = createI18n({ language: lang, locales: { fr: French } });
// to change dynamically the language:
// i18n.setLanguage("fr");
// to set the messages of the current set language (if we load the json async):
// i18n.setLocales(French);

const fetchScenes = async () => {
  const res = await fetch("/scenes.json");
  return res.json();
};

const [scenes, setScenes] = createStore<Array<SceneInfo>>([]);
export { scenes };

const App = () => {
  const data = createAsync<{ scenes: Array<SceneInfo> }>(fetchScenes);

  createEffect(() => {
    const dataJson = data();
    if (dataJson) {
      setScenes(dataJson.scenes);
    }
  });

  createEffect(() => {
    const lang = language();
    i18nCatalog.setLanguage(lang);
    localStorage.setItem("lang", lang);
    document.querySelector("html")?.setAttribute("lang", lang);
  });

  createEffect(() => {
    setPseudoWithoutDollar(pseudo().replace("$", ""));
  });

  const sessionQuery = useSession();
  const activeMemberRoleQuery = useActiveMemberRole(); // This gives a 400 on the server when user has not active organization

  createEffect((prevOrga) => {
    // In better-auth 1.4.5, using authClient.organization.setActive({ organizationId: someid })
    // refetches useSession and useActiveOrganization, but doesn't refetch useActiveMember or useActiveMemberRole
    const currentOrga = sessionQuery().data?.session.activeOrganizationId;
    if (prevOrga !== undefined && currentOrga !== undefined && prevOrga !== currentOrga) {
      untrack(activeMemberRoleQuery).refetch();
    }
    return currentOrga;
  });

  createEffect(() => {
    const currentSession = sessionQuery().data;
    if (currentSession) {
      const user = currentSession.user;
      setSession(reconcile({ user }));
      const userRole = user.role;
      // Check if user is a global admin
      setUserIsAdmin(userRole === "admin");
    } else {
      setSession(reconcile({ user: null }));
      setUserIsAdmin(false);
    }
  });

  return (
    <I18nProvider i18n={i18nCatalog}>
      <Router
        root={(props) => {
          return (
            <>
              <Title>{appName()}</Title>
              <AppNeedsUpdate />
              {/* <ErrorBoundary fallback={<div>Something went terribly wrong</div>}> */}
              {props.children}
              {/* </ErrorBoundary> */}
            </>
          );
        }}
      >
        {/* For Room component with preload={preloadSceneElementsAndServerConfig}, elements.json is fetched twice,
        the query cache doesn't seem to work for the fetchSceneElements call in scene-switcher aframe component */}
        <Route path="/r/:roomId/s/:scenarioLinkId" component={Room} preload={preloadServerConfig} />
        <Route path="/r/:roomId/s/:scenarioLinkId/editor" component={SimpleEditor} />
        <Route path="/r/:roomId/:sceneName?" component={Room} preload={preloadServerConfig} />
        <Route path="/r/:roomId/editor" component={SimpleEditor} />
        <Route path="/r/:roomId/ar" component={ARPage} />
        <Route path="/r/:roomId/:sceneName/ar" component={ARPage} preload={preloadSceneElements} />
        <Route path="/r/:roomId/:sceneName/mv" component={MVPage} preload={preloadSceneElements} />
        <Route path="/r/:roomId/:sceneName/editor" component={SimpleEditor} preload={preloadSceneElements} />
        <Route path="/r/:roomId/:tenantId/:sceneName" component={Room} preload={preloadServerConfig} />
        <Route path="/r/:roomId/:tenantId/:sceneName/editor" component={SimpleEditor} preload={preloadSceneElements} />
        <Route path="/r/:roomId/:tenantId/:sceneName/ar" component={ARPage} preload={preloadSceneElements} />
        <Route path="/r/:roomId/:sceneName/arbanner" component={ARBanner} preload={preloadSceneElements} />
        <Route path="/go/:roomId" component={Room} preload={preloadServerConfig} />

        <Route path="/" component={TenantLayout}>
          <Route path="/" component={Home} />
          <Route path="/login" component={Login} />
          <Route path="/error" component={ErrorPage} />
          <Route path="/magic-thanks" component={MagicThanks} />
          <Route path="/profile" component={Profile} />
          <Route path="/accept-invitation/:id" component={AcceptInvitation} />
          {/* Admin routes */}
          <Route path="/admin/users" component={AdminUsers} />
          <Route path="/admin/organizations" component={AdminOrganizations} />
          <Route path="/admin/organizations/:orgId/settings" component={OrganizationSettings} />
          <Route path="/admin/storage" component={AdminStorage} />
          <Route path="/admin/libraries" component={AdminLibraries} />
          <Route path="/admin/libraries/:id" component={LibraryEditor} />
        </Route>

        <Route path="/:tenantId" component={TenantLayout} preload={preloadTenantConfig}>
          <Route path="/" component={TenantHome} />
          <Route path="/projects" component={SavedRooms} />
          <Route path="/scenarios" component={Scenarios} />
          <Route path="/training-plans" component={TrainingPlans} />
          <Route path="/plan/:planId" component={PlanView} />
          <Route path="/saved-rooms" component={() => <Navigate href="../projects" />} />
          <Route path="/login" component={Login} />
          <Route path="/error" component={ErrorPage} />
          <Route path="/magic-thanks" component={MagicThanks} />
          <Route path="/profile" component={Profile} />
          <Route path="/settings" component={OrganizationSettings} />
          {/* Classes management moved to the training plans page */}
          <Route path="/classes" component={() => <Navigate href="../training-plans" />} />
          <Route path="/storage" component={Storage} />
          <Route path="/libraries" component={Libraries} />
          <Route path="/libraries/:id" component={LibraryEditor} />
        </Route>
      </Router>
    </I18nProvider>
  );
};

export default App;

// This code sample uses features introduced in Workbox v6.
const promptForUpdate = () => {
  return new Promise((resolve) => {
    setAppNeedsUpdate(true);
    createEffect(() => {
      if (appNeedsUpdateOk()) {
        resolve(true);
      }
      if (appNeedsUpdateDismiss()) {
        resolve(false);
      }
    });
  });
};

if (process.env.NODE_ENV === "production") {
  if ("serviceWorker" in navigator) {
    const wb = new Workbox("/service-worker.js");

    // @ts-ignore
    const showSkipWaitingPrompt = async () => {
      // Assuming the user accepted the update, set up a listener
      // that will reload the page as soon as the previously waiting
      // service worker has taken control.
      wb.addEventListener("controlling", () => {
        // At this point, reloading will ensure that the current
        // tab is loaded under the control of the new service worker.
        // Depending on your web app, you may want to auto-save or
        // persist transient state before triggering the reload.
        window.location.reload();
      });

      // When `event.wasWaitingBeforeRegister` is true, a previously
      // updated service worker is still waiting.
      // You may want to customize the UI prompt accordingly.

      // This code assumes your app has a promptForUpdate() method,
      // which returns true if the user wants to update.
      // Implementing this is app-specific; some examples are:
      // https://open-ui.org/components/alert.research or
      // https://open-ui.org/components/toast.research
      const updateAccepted = await promptForUpdate();

      if (updateAccepted) {
        wb.messageSkipWaiting();
      }
    };

    // Add an event listener to detect when the registered
    // service worker has installed but is waiting to activate.
    wb.addEventListener("waiting", () => {
      showSkipWaitingPrompt();
    });

    wb.register();
  }
}
